/* iceberg-ui */
import { PieChart } from '@auviknetworks/iceberg-ui';

/* MUI */
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Box,
    IconButton,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { popoverContentsColumns } from 'application/Shared/Components/Recharts/PopoverContents';
import { ManagedTable } from 'application/Shared/Components/Table';
import { HMS } from 'application/Shared/Components/Usage';

/*  Yarn  */
import i18n from 'i18n';

/* Objects */
import { Application } from 'objects/Application';

/* React */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

const CategoryReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup } = props;

    if (typeof reportData === 'undefined') {
        return null;
    }

    return (
        <>
            {reportData.organizationalCategoryUsage && (
                <CategoryUsage
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.organizationalCategoryUsage}
                />
            )}
            {reportData.organizationalCategoryUsageBreakdown && (
                <CategoryUsageBreakdown
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.organizationalCategoryUsageBreakdown}
                />
            )}
            {reportData.individualCategoryUsage && (
                <CategoryUsage
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.individualCategoryUsage}
                />
            )}
            {reportData.individualCategoryUsageBreakdown && (
                <CategoryUsageBreakdown
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.individualCategoryUsageBreakdown}
                />
            )}
        </>
    );
};

const CategoryUsage = (props) => {
    const { renderingScheduledReport, reportData, t } = props;

    const categories = [];
    const subCategories = [];
    Object.entries(reportData).forEach(([category, values]) => {
        // TODO: Localize categories and subcategories so that one can use
        // name: t(`Categories.${category}`) and below, name: t(`Categories.${subcat}`)
        categories.push({ name: category, value: values.usage });
        Object.entries(values.subCategories).forEach(([subcat, value]) => {
            subCategories.push({
                name: subcat,
                value
            });
        });
    });

    return (
        <Box mt="32px" width="100%">
            <Typography variant="h5">
                {t('Reports.CategoryReport.category_overview')}
            </Typography>
            <PieChart
                data={categories}
                emptyMessage={t('Common.noData')}
                isAnimationActive={!renderingScheduledReport}
                popoverContentsColumns={popoverContentsColumns}
                responsiveContainerHeight={250}
            />
        </Box>
    );
};

const headers = [
    {
        align: 'left',
        field: 'ilo_uncollapse',
        renderHeader: <strong />,
        width: '5%'
    },
    {
        align: 'left',
        field: 'ilo_cat',
        renderHeader: <strong>{i18n.t('Common.category')}</strong>,
        width: '40%'
    },
    {
        align: 'left',
        field: 'ilo_app',
        renderHeader: <strong>{i18n.t('Common.applications')}</strong>,
        width: '15%'
    },
    {
        align: 'right',
        field: 'ilo_usage',
        renderHeader: <strong>{i18n.t('Common.usage')}</strong>,
        width: '15%'
    },
    {
        align: 'right',
        field: 'ilo_percent',
        renderHeader: <strong>{i18n.t('Common.percent')}</strong>,
        width: '15%'
    }
];

class CategoryUsageBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            applications: {},
            categoryReport: [],
            totalUsage: 0
        };
    }

    componentDidMount() {
        const { reportData } = this.props;
        this.setState({
            applications: reportData.applications,
            categoryReport: reportData.categoryReport,
            totalUsage: reportData.totalUsage
        });
    }

    setCategoryOpen = (row) => {
        const { categoryReport } = this.state;

        const updatedCatReport = categoryReport.map((category) => {
            if (row.name === category.name) {
                return {
                    ...category,
                    open: !category.open
                };
            }
            return { ...category };
        });

        this.setState({ categoryReport: updatedCatReport });
    };

    setSubCategoryOpen = (row, subRow) => {
        const { categoryReport } = this.state;
        const updatedCatReport = categoryReport.map((category) => {
            if (row.name === category.name) {
                const updatedSubcatReport = category.subCategory.map(
                    (subcat) => {
                        if (subRow.name === subcat.name) {
                            return {
                                ...subcat,
                                open: !subcat.open
                            };
                        }
                        return { ...subcat };
                    }
                );

                return {
                    ...category,
                    subCategory: updatedSubcatReport
                };
            }
            return { ...category };
        });

        this.setState({ categoryReport: updatedCatReport });
    };

    renderRow = (row) => {
        const { renderingScheduledReport, t } = this.props;
        const { applications, totalUsage } = this.state;
        return (
            <React.Fragment key={`ilo_${row.name}`}>
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    <TableCell>
                        {!renderingScheduledReport && (
                            <IconButton
                                data-trackid={
                                    row.open
                                        ? 'reports_category_collapseRow'
                                        : 'reports_category_expandRow'
                                }
                                aria-label="expand row"
                                size="small"
                                onClick={() => {
                                    this.setCategoryOpen(row);
                                }}
                                sx={{ color: 'icon.textColor' }}
                            >
                                {row.open ? (
                                    <KeyboardArrowUp />
                                ) : (
                                    <KeyboardArrowDown />
                                )}
                            </IconButton>
                        )}
                    </TableCell>
                    <TableCell>
                        <strong>
                            {row.name}{' '}
                            {!renderingScheduledReport &&
                                `(${row.subCategory.length})`}
                        </strong>
                    </TableCell>
                    <TableCell>
                        <strong>
                            {!row.open &&
                                row.subCategory.reduce(
                                    (total, category) =>
                                        total + category.applications.length,
                                    0
                                )}
                        </strong>
                    </TableCell>
                    <TableCell align="right">
                        <strong>
                            <HMS seconds={row.usage} />
                        </strong>
                    </TableCell>
                    <TableCell align="right">
                        <strong>
                            {totalUsage === 0
                                ? 0
                                : Math.round((row.usage / totalUsage) * 10000) /
                                  100}
                            %
                        </strong>
                    </TableCell>
                </TableRow>

                {(row.open || renderingScheduledReport) &&
                    row.subCategory.map((data) => (
                        <React.Fragment key={`ilo_subcategory_${data.name}`}>
                            <TableRow>
                                <TableCell>&nbsp;</TableCell>
                                <TableCell>
                                    {!renderingScheduledReport && (
                                        <IconButton
                                            data-trackid={
                                                data.open
                                                    ? 'reports_category_collapseRow'
                                                    : 'reports_category_expandRow'
                                            }
                                            aria-label="expand row"
                                            size="small"
                                            onClick={() => {
                                                this.setSubCategoryOpen(
                                                    row,
                                                    data
                                                );
                                            }}
                                            sx={{
                                                color: 'icon.textColor'
                                            }}
                                        >
                                            {data.open ? (
                                                <KeyboardArrowUp />
                                            ) : (
                                                <KeyboardArrowDown />
                                            )}
                                        </IconButton>
                                    )}
                                    {renderingScheduledReport && <> - </>}
                                    {data.name}
                                </TableCell>
                                <TableCell align="left">
                                    {!data.open && data.applications.length}
                                </TableCell>
                                <TableCell align="right">
                                    <HMS seconds={data.usage} />
                                </TableCell>
                                <TableCell align="right">
                                    {row?.usage === 0
                                        ? 0
                                        : Math.round(
                                              (data.usage / row.usage) * 10000
                                          ) / 100}
                                    %
                                </TableCell>
                            </TableRow>
                            {(data.open || renderingScheduledReport) &&
                                data.applications.map((app) => (
                                    <TableRow
                                        sx={{
                                            backgroundColor:
                                                'table.subtableBackground'
                                        }}
                                        key={`ilo_application_${app.id}`}
                                    >
                                        <TableCell>&nbsp;</TableCell>
                                        <TableCell align="left" colSpan={2}>
                                            <Application
                                                data={
                                                    applications[app.id]
                                                        .application
                                                }
                                                fromPage={t(
                                                    'Reports.Title.organizational.report'
                                                )}
                                                nameHasLink
                                            >
                                                <ApplicationNameLinkCell />
                                            </Application>
                                        </TableCell>
                                        <TableCell align="right">
                                            <HMS seconds={app.usage} />
                                        </TableCell>
                                        <TableCell align="right">
                                            {Math.round(
                                                (app.usage / data.usage) * 10000
                                            ) / 100}
                                            %
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </React.Fragment>
                    ))}
            </React.Fragment>
        );
    };

    render() {
        const { t } = this.props;
        const { categoryReport } = this.state;

        return (
            <Box className="break-auto" mt="32px">
                <Typography variant="h5">
                    {t('Reports.CategoryReport.category_breakdown')}
                </Typography>
                <ManagedTable
                    headers={headers}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey="name"
                    renderFooter={false}
                    rows={categoryReport}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="usage"
                />
            </Box>
        );
    }
}

export default withTranslation()(CategoryReports);
